import React, { type ReactNode } from 'react';
import { tv } from '@/utils/tv';
import { Spinner } from '@/components/Spinner';
import {
  BorderRadius,
  ButtonType,
  ButtonVariant,
  Size,
} from '@/types/components';

const button = tv({
  base: 'relative flex cursor-pointer items-center justify-center gap-2 transition-colors disabled:cursor-not-allowed',
  variants: {
    variant: {
      [ButtonVariant.Primary]:
        'bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-pressed disabled:bg-primary-disabled disabled:text-primary-foreground/60',
      [ButtonVariant.Secondary]:
        'bg-secondary text-primary hover:bg-secondary-hover active:bg-secondary-pressed disabled:bg-secondary/40 disabled:text-muted-foreground/40',
    },
    size: {
      [Size.Large]: 'h-12 px-4 text-callout-16',
      [Size.Medium]: 'h-10 px-4 text-callout-16',
      [Size.Small]: 'h-8 px-3 text-subhead-14',
    },
    radius: {
      [BorderRadius.Small]: 'rounded-xl',
      [BorderRadius.Medium]: 'rounded-2xl',
      [BorderRadius.Pill]: 'rounded-3xl',
    },
  },
});

const defaultRadiusBySize: Record<Size, BorderRadius> = {
  [Size.Large]: BorderRadius.Small,
  [Size.Medium]: BorderRadius.Pill,
  [Size.Small]: BorderRadius.Medium,
};

interface Props {
  children: ReactNode;
  variant: ButtonVariant;
  size?: Size;
  radius?: BorderRadius;
  disabled?: boolean;
  isLoading?: boolean;
  type?: ButtonType;
  className?: string;
  onClick?: () => void;
}

export const Button: React.FC<Props> = ({
  children,
  variant,
  size = Size.Medium,
  radius,
  disabled = false,
  isLoading = false,
  type = ButtonType.Button,
  className,
  onClick,
}) => (
  <button
    type={type}
    disabled={disabled || isLoading}
    onClick={onClick}
    className={button({
      variant,
      size,
      radius: radius ?? defaultRadiusBySize[size],
      className,
    })}
  >
    {isLoading && (
      <span className="absolute inset-0 flex items-center justify-center">
        <Spinner />
      </span>
    )}
    <span
      className={`flex items-center justify-center gap-2 ${isLoading ? 'invisible' : ''}`}
    >
      {children}
    </span>
  </button>
);
